import { createFileRoute } from "@tanstack/react-router";
import {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  useId,
} from "react";
import {
  Plus,
  FileSignature,
  AlertCircle,
  RefreshCw,
  Loader2,
  X,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTRPCClient } from "@/trpc";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { z } from "zod";
import { SpecialtiesSelectObject } from "@/components/Selects/SpecialtiesSelectObject";
import { useOrgPath } from "@/hooks/useOrgPath";
import { OrganizationJobPostListItem } from "@/features/job-post/components/organization-job-post-list-item";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/jobs/post/",
)({
  component: JobsPage,
  validateSearch: z.object({
    sortBy: z
      .enum(["alphabetical", "createdAt", "recentlyInterested"])
      .optional()
      .default("recentlyInterested"),
    status: z.enum(["active", "closed"]).optional().default("active"),
    searchTerm: z.string().optional(),
    specialtyId: z.string().optional(),
    facilityId: z.string().optional(),
  }),
});

function JobsPage() {
  const trpcClient = useTRPCClient();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = Route.useRouteContext();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const orgPath = useOrgPath();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const filterId = useId();

  // Local state for search input with debouncing
  const [localSearchTerm, setLocalSearchTerm] = useState(
    search.searchTerm || "",
  );
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Local state for specialty select (to preserve the name for display)
  const [selectedSpecialty, setSelectedSpecialty] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Clear specialty state when URL param is cleared
  useEffect(() => {
    if (!search.specialtyId) {
      setSelectedSpecialty(null);
    }
  }, [search.specialtyId]);

  const PAGE_SIZE = 20;

  // Infinite query for job posts with server-side filtering
  const {
    data: infiniteData,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      "jobPost.orgList",
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        sortBy: search.sortBy,
        status: search.status,
        searchTerm: search.searchTerm,
        specialtyId: search.specialtyId,
        facilityId: search.facilityId,
      },
    ],
    queryFn: ({ pageParam = 0 }) =>
      trpcClient.jobPost.orgList.query({
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        sortBy: search.sortBy,
        status: search.status,
        searchTerm: search.searchTerm,
        specialtyId: search.specialtyId,
        facilityId: search.facilityId,
        offset: pageParam * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.hasMore) {
        return allPages.length;
      }
      return undefined;
    },
    initialPageParam: 0,
    enabled: !!selectedOrgBusinessUnit.id,
  });

  // Flatten paginated data and deduplicate (offset pagination can produce
  // duplicates when data changes between page fetches)
  const jobData = useMemo(() => {
    const all = infiniteData?.pages.flatMap((page) => page.jobs) ?? [];
    const seen = new Set<string>();
    return all.filter((job) => {
      if (seen.has(job.id)) {
        return false;
      }
      seen.add(job.id);
      return true;
    });
  }, [infiniteData]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Extract unique facilities from job data for filter dropdown
  const getFacilityOptions = () => {
    if (!jobData) {
      return [];
    }

    const facilityMap = new Map<string, string>();

    jobData.forEach((job) => {
      if (job.facility?.id && job.facility?.name) {
        facilityMap.set(job.facility.id, job.facility.name);
      }
    });

    return Array.from(facilityMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const facilities = getFacilityOptions();

  const hasFilters = useMemo(() => {
    return !!(
      (
        search.specialtyId ||
        search.facilityId ||
        (search.searchTerm && search.searchTerm.trim()) ||
        search.status === "closed"
      ) // "closed" is a filter since "active" is default
    );
  }, [search.specialtyId, search.facilityId, search.searchTerm, search.status]);

  const statusOptions = [
    { value: "active" as const, label: "Active" },
    { value: "closed" as const, label: "Closed" },
  ];

  // Navigation handlers for filters (all update URL search params)
  const handleSortChange = (
    value: "alphabetical" | "createdAt" | "recentlyInterested",
  ) => {
    navigate({
      to: orgPath("/jobs/post"),
      search: {
        ...search,
        sortBy: value,
      },
    });
  };

  const handleStatusChange = (value: "active" | "closed") => {
    navigate({
      to: orgPath("/jobs/post"),
      search: {
        ...search,
        status: value,
      },
    });
  };

  const handleSearchTermChange = useCallback(
    (value: string) => {
      // Update local state immediately for responsive UI
      setLocalSearchTerm(value);

      // Debounce the URL navigation to reduce API calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        navigate({
          to: orgPath("/jobs/post"),
          search: {
            ...search,
            searchTerm: value || undefined,
          },
        });
      }, 300);
    },
    [navigate, search, orgPath],
  );

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSpecialtyChange = (
    value: { name: string; id: string } | { name: string; id: string }[] | null,
  ) => {
    // Handle single select (not array)
    const specialty = Array.isArray(value) ? value[0] : value;
    // Save to local state for display
    setSelectedSpecialty(specialty || null);
    // Update URL for filtering
    navigate({
      to: orgPath("/jobs/post"),
      search: {
        ...search,
        specialtyId: specialty?.id || undefined,
      },
    });
  };

  const handleFacilityChange = (value: string) => {
    navigate({
      to: orgPath("/jobs/post"),
      search: {
        ...search,
        facilityId: value === "all" ? undefined : value,
      },
    });
  };

  const clearFilters = () => {
    // Clear local state
    setLocalSearchTerm("");
    setSelectedSpecialty(null);
    // Clear URL params
    navigate({
      to: orgPath("/jobs/post"),
      search: {
        sortBy: search.sortBy,
        status: search.status,
      },
    });
  };

  return (
    <div className="min-w-0 flex-1 bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-none pb-2">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Job posts
            </p>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">
              Manage job posts
            </h1>
            <p className="text-slate-500 max-w-xl text-sm sm:text-base">
              A clear snapshot of openings and what needs attention.
            </p>
          </div>
          {hasPermission(Permission.ManageJobPosts) && (
            <Button asChild className="w-full gap-2 font-semibold sm:w-auto">
              <Link to={orgPath("/jobs/post/create")}>
                <Plus aria-hidden="true" className="size-4 shrink-0" />
                Create Job Post
              </Link>
            </Button>
          )}
        </div>

        <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold text-slate-800">
              Refine results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid min-w-0 grid-cols-1 items-end gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <div className="grid min-w-0 gap-2 sm:col-span-2">
                <Label htmlFor={`${filterId}-search`}>Search</Label>
                <div className="relative min-w-0">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Search className="h-4 w-4 text-slate-400" />
                  </div>
                  <Input
                    id={`${filterId}-search`}
                    type="text"
                    placeholder="Search job posts..."
                    value={localSearchTerm}
                    onChange={(e) => handleSearchTermChange(e.target.value)}
                    className="min-w-0 pl-10 pr-10"
                  />
                  {localSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSearchTermChange("")}
                      aria-label="Clear job search"
                      className="absolute inset-y-0 right-0 px-3 h-full text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid min-w-0 gap-2">
                <Label htmlFor={`${filterId}-sort`}>Sort by</Label>
                <Select value={search.sortBy} onValueChange={handleSortChange}>
                  <SelectTrigger
                    id={`${filterId}-sort`}
                    className="min-w-0 text-left [&>span]:min-w-0 [&>span]:truncate"
                  >
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alphabetical">Alphabetical</SelectItem>
                    <SelectItem value="createdAt">Created Date</SelectItem>
                    <SelectItem value="recentlyInterested">
                      Recently Interested
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <fieldset className="min-w-0">
                <legend className="mb-2 text-sm font-medium leading-5 text-slate-700">
                  Status
                </legend>
                <div className="grid h-12 grid-cols-2 rounded-xl border border-slate-200 bg-slate-100">
                  {statusOptions.map((option) => (
                    <label
                      key={option.value}
                      htmlFor={`${filterId}-status-${option.value}`}
                      className="relative flex h-full min-w-0 cursor-pointer select-none p-1"
                    >
                      <input
                        id={`${filterId}-status-${option.value}`}
                        type="radio"
                        name={`${filterId}-status`}
                        value={option.value}
                        checked={search.status === option.value}
                        onChange={() => handleStatusChange(option.value)}
                        className="peer sr-only"
                      />
                      <span className="flex min-w-0 flex-1 items-center justify-center rounded-lg px-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 peer-checked:bg-white peer-checked:text-primary peer-checked:shadow-xs peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-slate-100">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="grid min-w-0 gap-2">
                <Label htmlFor={`${filterId}-specialty`}>Specialty</Label>
                <SpecialtiesSelectObject
                  inputId={`${filterId}-specialty`}
                  value={selectedSpecialty || undefined}
                  onChange={handleSpecialtyChange}
                  placeholder="All specialties"
                  isMulti={false}
                />
              </div>
              {facilities.length > 0 && (
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor={`${filterId}-facility`}>Facility</Label>
                  <Select
                    value={search.facilityId || "all"}
                    onValueChange={handleFacilityChange}
                  >
                    <SelectTrigger
                      id={`${filterId}-facility`}
                      className="min-w-0 text-left [&>span]:min-w-0 [&>span]:truncate"
                    >
                      <SelectValue placeholder="All facilities" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      <SelectItem value="all">All Facilities</SelectItem>
                      {facilities.map((facility) => (
                        <SelectItem
                          key={facility.id}
                          value={facility.id}
                          className="whitespace-normal [overflow-wrap:anywhere]"
                        >
                          {facility.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {hasFilters && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className="font-semibold uppercase tracking-wide text-slate-500">
                  Active filters
                </span>
                {search.specialtyId && selectedSpecialty && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 text-slate-700"
                  >
                    Specialty: {selectedSpecialty.name}
                  </Badge>
                )}
                {search.facilityId && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 text-slate-700"
                  >
                    Facility:{" "}
                    {facilities.find((f) => f.id === search.facilityId)?.name ||
                      search.facilityId}
                  </Badge>
                )}
                {search.searchTerm && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 text-slate-700"
                  >
                    Search: {search.searchTerm}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-7 px-3 text-slate-500 hover:text-slate-700"
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Clear all
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4 sm:space-y-6">
          {isLoading ? (
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
              <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-primary/5 p-4 rounded-2xl mb-6">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                  Loading job postings
                </h3>
                <p className="text-slate-600 max-w-md text-sm sm:text-base">
                  Please wait while we fetch the latest openings for your team.
                </p>
              </CardContent>
            </Card>
          ) : isError ? (
            <Card className="rounded-2xl border-red-200 bg-red-50 shadow-xs">
              <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-red-100 p-4 rounded-full mb-6">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                  Failed to load job postings
                </h3>
                <p className="text-slate-600 mb-6 max-w-md text-sm sm:text-base">
                  {error instanceof Error
                    ? error.message
                    : "An unexpected error occurred while loading job postings. Please try again."}
                </p>
                <Button className="font-semibold" onClick={() => refetch()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : jobData && jobData.length > 0 ? (
            <>
              {/* Show subtle loading indicator when refetching due to filter changes */}
              {isFetching && !isFetchingNextPage && (
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Updating results...</span>
                </div>
              )}
              <div className="grid min-w-0 gap-3 sm:gap-4">
                {jobData.map((job) => (
                  <OrganizationJobPostListItem
                    key={job.id}
                    job={job}
                    userId={user?.id}
                    canManageJobPosts={hasPermission(Permission.ManageJobPosts)}
                  />
                ))}
              </div>

              {/* Infinite scroll trigger */}
              {hasNextPage && (
                <div
                  ref={loadMoreRef}
                  className="flex items-center justify-center py-6"
                >
                  {isFetchingNextPage ? (
                    <div className="flex items-center space-x-2 text-slate-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-sm font-medium">
                        Loading more jobs...
                      </span>
                    </div>
                  ) : (
                    <div className="h-4" />
                  )}
                </div>
              )}
            </>
          ) : hasFilters ? (
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
              <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-primary/5 p-4 rounded-2xl mb-6">
                  <FileSignature className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                  No job postings match your filters
                </h3>
                <p className="text-slate-600 mb-6 max-w-md text-sm sm:text-base">
                  Adjust your filters or clear them to explore all available job
                  posts.
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Clear filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
              <CardContent className="p-8 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-primary/5 p-4 rounded-2xl mb-6">
                  <FileSignature className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                  No job postings yet
                </h3>
                <p className="text-slate-600 mb-6 max-w-md text-sm sm:text-base">
                  {hasPermission(Permission.ManageJobPosts)
                    ? "Create your first job post to start attracting qualified healthcare providers to your organization."
                    : "No job posts have been created yet. Contact an admin to create job posts."}
                </p>
                {hasPermission(Permission.ManageJobPosts) && (
                  <Button
                    asChild
                    className="h-auto min-h-12 w-full gap-2 whitespace-normal text-center font-semibold sm:w-auto"
                  >
                    <Link to={orgPath("/jobs/post/create")}>
                      <Plus aria-hidden="true" className="size-4 shrink-0" />
                      Create your first job post
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
